/**
 * Module Vuex spécialisé pour la gestion des détails d'inventaire
 * Respecte le principe de responsabilité unique (SRP)
 */

import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import DetailInventaireService from '@/Services/DetailInventaireService';
import { DetailInventaire } from '@/models/DetailInventaire';
import { isErrorResponse } from '@/utils/typeGuards';

@Module({ namespaced: true })
export default class DetailInventaireModule extends VuexModule {
    items: DetailInventaire[] = [];
    error: string | null = null;
    item: DetailInventaire | null = null;

    @Mutation
    setItems(items: DetailInventaire[]) {
        this.items = items;
    }

    @Mutation
    setItem(item: DetailInventaire) {
        this.item = item;
    }

    @Mutation
    setError(error: string | null) {
        this.error = error;
    }

    @Action
    async fetchDetail(url: string) {
        try {
            console.log('🔍 DETAIL INVENTAIRE MODULE - URL reçue:', url);
            
            const response = await DetailInventaireService.getAll(url);
            
            if (isErrorResponse(response)) {
                this.context.commit('setError', response.message || 'An error occurred while fetching detail inventaire.');
                return null;
            }
            
            // Stocker les données dans Vuex
            this.context.commit('setItems', response.data);
            
            console.log('🔍 DETAIL INVENTAIRE MODULE - Données reçues:', {
                dataLength: response.data?.length,
                totalRows: response.totalRows,
                recordsFiltered: response.recordsFiltered,
                recordsTotal: response.recordsTotal
            });
            
            // Retourner la réponse complète pour supporter DataTables
            return {
                data: response.data,
                totalRows: response.totalRows,
                recordsFiltered: response.recordsFiltered,
                recordsTotal: response.recordsTotal,
                draw: response.draw
            };
        } catch (error: any) {
            console.error('🔍 DETAIL INVENTAIRE MODULE - Erreur:', error);
            this.context.commit('setError', error.message || 'An error occurred while fetching detail inventaire.');
            return null;
        }
    }

    @Action
    async fetchDetailInfo(id: number) {
        try {
            console.log('🔍 DETAIL INVENTAIRE MODULE - fetchDetailInfo ID:', id);
            
            const response = await DetailInventaireService.getInfo(id);
            
            if (isErrorResponse(response)) {
                this.context.commit('setError', response.message || 'An error occurred while fetching inventaire info.');
                return null;
            }
            
            return response;
        } catch (error: any) {
            console.error('🔍 DETAIL INVENTAIRE MODULE - Erreur fetchDetailInfo:', error);
            this.context.commit('setError', error.message || 'An error occurred while fetching inventaire info.');
            throw error;
        }
    }

    @Action
    async extractAllDetail({ id, page, pageSize, sortBy, sortOrder, search }: { 
        id: number; 
        page: number; 
        pageSize: number; 
        sortBy: string; 
        sortOrder: string; 
        search: string 
    }) {
        try {
            const response = await DetailInventaireService.extractAll({ id, page, pageSize, sortBy, sortOrder, search });
            return response; // Retourner directement la réponse blob
        } catch (error: any) {
            this.context.commit('setError', error.message || 'An error occurred while extracting detail inventaire.');
            throw error;
        }
    }

    @Action
    async getDetailInventaireEmplacement({ id, page, pageSize, sortBy, sortOrder, search }: { 
        id: number; 
        page: number; 
        pageSize: number; 
        sortBy: string; 
        sortOrder: string; 
        search: string 
    }) {
        try {
            console.log('🔍 DETAIL INVENTAIRE MODULE - getDetailInventaireEmplacement params:', { id, page, pageSize, sortBy, sortOrder, search });
            
            const response = await DetailInventaireService.getDetailInventaireEmplacement({ id, page, pageSize, sortBy, sortOrder, search });
            
            if (isErrorResponse(response)) {
                this.context.commit('setError', response.message || 'An error occurred while fetching detail inventaire emplacement.');
                return null;
            }
            
            // Stocker les données dans Vuex
            this.context.commit('setItems', response.data);
            
            console.log('🔍 DETAIL INVENTAIRE MODULE - Données reçues (getDetailInventaireEmplacement):', {
                dataLength: response.data?.length,
                totalRows: response.totalRows,
                recordsFiltered: response.recordsFiltered,
                recordsTotal: response.recordsTotal
            });
            
            // Retourner la réponse complète pour supporter DataTables
            return {
                data: response.data,
                totalRows: response.totalRows,
                recordsFiltered: response.recordsFiltered,
                recordsTotal: response.recordsTotal,
                draw: response.draw
            };
        } catch (error: any) {
            console.error('🔍 DETAIL INVENTAIRE MODULE - Erreur getDetailInventaireEmplacement:', error);
            this.context.commit('setError', error.message || 'An error occurred while fetching detail inventaire emplacement.');
            throw error;
        }
    }
    @Action
    async launchInventaire(ids: number[]) {
        try {
            const response = await DetailInventaireService.launchInventaire(ids);
            return response;
        } catch (error: any) {
            this.context.commit('setError', error.message || 'An error occurred while launching inventaire.');
            throw error;
        }
    }

    get getAll() {
        return this.items;
    }

    get getItem() {
        return this.item;
    }
}